import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { MapPin, Users, Ticket, ShieldCheck, CreditCard, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import { useMonetizationState } from "../lib/monetization";
import PaymentMethodsBadge from "../components/PaymentMethodsBadge";

export default function CommerceDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [activeTickets, setActiveTickets] = useState([]);
  const [saveCard, setSaveCard] = useState(false);
  const [queues, setQueues] = useState([]);
  const [selectedQueueId, setSelectedQueueId] = useState(null);
  const [myStatus, setMyStatus] = useState(null); // { canTake, reason, message, lastTicketNumber, lastValidatedAt }
  const { role, visitorAccountsEnabled, refresh, commerce } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const monet = useMonetizationState();
  const isFreeMode = !monet.visitorsPaid;

  // Pre-select queue from query string ?queueId=... + welcome toast when
  // the visitor arrives via a QR scan (?from=qr — set by PublicQueueLanding
  // and GlobalQueueLanding). One-shot per session : we strip `from=qr` from
  // the URL after firing the toast so a refresh doesn't show it again.
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const qParam = params.get("queueId");
    if (qParam) setSelectedQueueId(qParam);
    if (params.get("from") === "qr") {
      // Defer the toast until we have the commerce name (handled below).
      sessionStorage.setItem("bypassgo_qr_welcome_pending", "1");
      params.delete("from");
      const clean = params.toString();
      nav(`${loc.pathname}${clean ? `?${clean}` : ""}`, { replace: true });
    }
    // eslint-disable-next-line
  }, [loc.search]);

  // Fire the welcome toast once we know the commerce name (after `load()`).
  useEffect(() => {
    if (!c?.name) return;
    if (sessionStorage.getItem("bypassgo_qr_welcome_pending") !== "1") return;
    sessionStorage.removeItem("bypassgo_qr_welcome_pending");
    toast(`👋 Bienvenue chez ${c.name}`, {
      description: "Votre ticket vous attend, prenez-le en un clic.",
      duration: 4500,
    });
  }, [c?.name]);

  const load = async () => {
    try {
      const [{ data }, qsRes] = await Promise.all([
        api.get(`/commerces/${id}`),
        api.get(`/commerces/${id}/queues`).catch(() => ({ data: [] })),
      ]);
      setC(data);
      setQueues(qsRes.data || []);
      // Use functional updater to avoid stale-closure overriding the user's
      // current selection on each polling tick (see fix for queue-auto-revert bug).
      // Default selection: the FIRST OPEN queue (spec utilisateur 2026-02).
      // Fallback to the first queue if none is open (rare — establishment
      // with all queues currently closed).
      setSelectedQueueId((prev) => {
        if (prev) return prev;
        const list = qsRes.data || [];
        const firstOpen = list.find((q) => q.isOpen && !q.isSuspended);
        return (firstOpen?.id) ?? (list[0]?.id ?? null);
      });
    } finally {
      setLoading(false);
    }
  };

  const loadActiveTickets = async () => {
    if (role !== "client") { setActiveTickets([]); return; }
    try {
      const { data } = await api.get("/tickets/my-active-list");
      setActiveTickets(Array.isArray(data) ? data : []);
    } catch {
      setActiveTickets([]);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    loadActiveTickets();
    const t = setInterval(loadActiveTickets, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [role, id]);

  // Persistent block check : if the visitor has already been served on the
  // selected queue's current open session, show a blocking banner BEFORE
  // they try to take a ticket. Re-checked every 5s and on queue change.
  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      if (role !== "client" || !selectedQueueId) { setMyStatus(null); return; }
      try {
        const { data } = await api.get(`/queues/${selectedQueueId}/my-status`);
        if (mounted) setMyStatus(data);
      } catch { if (mounted) setMyStatus(null); }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 5000);
    return () => { mounted = false; clearInterval(t); };
  }, [role, selectedQueueId]);

  const MAX_ACTIVE_TICKETS = 3;
  const activeCount = activeTickets.length;
  const hasReachedMax = activeCount >= MAX_ACTIVE_TICKETS;
  const ticketOnSelectedQueue = activeTickets.find(
    (t) => t.queueId === selectedQueueId,
  ) || null;
  const otherTickets = activeTickets.filter(
    (t) => t.queueId !== selectedQueueId,
  );

  const takeTicket = async () => {
    // An establishment account is NOT allowed to take a visitor ticket.
    // The merchant must log out first (or use a different device/browser
    // in pure visitor mode). Per 2026-02 spec.
    if (role === "commerce") {
      toast.error(
        "Vous êtes connecté en tant qu'établissement. Pour prendre un ticket, déconnectez-vous de votre compte.",
        { duration: 6000 },
      );
      return;
    }
    // Spec utilisateur Feb 2026 — bloc explicite quand la file est
    // suspendue par l'établissement : popup orange 3 s informant le
    // visiteur, et arrêt avant tout appel backend. La file peut être
    // principale, ponctuelle ou supplémentaire — même comportement.
    // Vérifié ici (avant les branches anonyme / login) pour s'appliquer
    // à TOUS les visiteurs (connecté, anonyme, ou non connecté).
    const selQueueNow = queues.find((q) => q.id === selectedQueueId);
    if (selQueueNow?.isSuspended) {
      toast(
        "La file est suspendue par l'établissement. Vous ne pourrez pas prendre de ticket tant qu'elle ne sera pas réactivée.",
        {
          duration: 3000,
          style: {
            background: "#FFF4DD",
            color: "#A65800",
            border: "2px solid #F18F18",
            fontWeight: 600,
          },
          className: "queue-suspended-toast",
        },
      );
      return;
    }
    // "Mode sans compte" — when the admin has disabled visitor accounts,
    // the visitor takes a ticket anonymously: the backend auto-creates a
    // guest user and returns a token we store locally so the visitor can
    // still poll their ticket and call /my-ticket etc. without any
    // login/register friction.
    if (role !== "client" && !visitorAccountsEnabled) {
      setCreating(true);
      try {
        const { data } = await api.post("/tickets/anonymous", {
          commerceId: id,
          type: "normal",
          queueId: selectedQueueId || undefined,
        });
        if (data?.token) {
          localStorage.setItem("bypassgo_token", data.token);
          // Backup the guest token in a separate key so the visitor never
          // loses their ticket after logging into an establishment account
          // on the same browser (logout restores from this backup).
          localStorage.setItem("bypassgo_guest_token", data.token);
          await refresh();
        }
        toast.success("Ticket pris — bonne attente !");
        nav("/my-ticket");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Impossible de prendre le ticket.");
      } finally {
        setCreating(false);
      }
      return;
    }
    if (role !== "client") return nav(`/login?next=${encodeURIComponent(`/commerce/${id}`)}`);
    if (ticketOnSelectedQueue) {
      toast.error("Vous avez déjà un ticket actif dans cette file. Un seul ticket par file est autorisé.");
      nav("/my-ticket");
      return;
    }
    if (hasReachedMax) {
      toast.error(`Vous avez déjà ${MAX_ACTIVE_TICKETS} tickets actifs. Annulez-en un pour en prendre un nouveau.`);
      nav("/my-ticket");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/payments/checkout/priority-ticket", {
        commerceId: id,
        origin_url: window.location.origin,
        saveCard,
        queueId: selectedQueueId || undefined,
      });
      // FREE MODE : ticket is created immediately, no Stripe redirect
      if (data?.free || data?.freeMode) {
        toast.success("Ticket enregistré (gratuit) — bonne attente !");
        nav("/my-ticket");
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur paiement");
      setCreating(false);
    }
  };

  if (loading) return <div className="container mx-auto px-4 py-16 text-center text-subtle">Chargement…</div>;
  if (!c) return <div className="container mx-auto px-4 py-16 text-center">Établissement introuvable</div>;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 max-w-5xl">
      {(ticketOnSelectedQueue || hasReachedMax || otherTickets.length > 0) && (
        <div
          className={`mb-6 border-2 ${ticketOnSelectedQueue || hasReachedMax ? "border-destructive bg-destructive/5" : "border-brand bg-brand/5"} px-4 py-3 flex items-center gap-3`}
          data-testid="active-tickets-banner"
        >
          <AlertTriangle size={18} className={`${ticketOnSelectedQueue || hasReachedMax ? "text-destructive" : "text-brand"} shrink-0`} />
          <div className="flex-1 min-w-0 text-sm leading-snug">
            {ticketOnSelectedQueue ? (
              <>
                <p className="font-bold text-ink">
                  Vous avez déjà un ticket actif (n°{ticketOnSelectedQueue.number}) dans cette file.
                </p>
                <p className="text-xs text-subtle mt-0.5">
                  Un seul ticket par file. Choisissez une autre file ou un autre établissement.
                </p>
              </>
            ) : hasReachedMax ? (
              <>
                <p className="font-bold text-ink">
                  Vous avez atteint la limite de {MAX_ACTIVE_TICKETS} tickets actifs.
                </p>
                <p className="text-xs text-subtle mt-0.5">
                  Annulez-en un pour en prendre un nouveau ici.
                </p>
              </>
            ) : (
              <>
                <p className="font-bold text-ink">
                  Vous avez {activeCount} ticket{activeCount > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""} sur d'autres files.
                </p>
                <p className="text-xs text-subtle mt-0.5">
                  Vous pouvez prendre jusqu'à {MAX_ACTIVE_TICKETS} tickets simultanément ({MAX_ACTIVE_TICKETS - activeCount} restant{MAX_ACTIVE_TICKETS - activeCount > 1 ? "s" : ""}).
                </p>
              </>
            )}
          </div>
          <Link
            to="/my-ticket"
            className="shrink-0 inline-flex items-center h-9 px-3 bg-ink text-white text-xs font-bold uppercase tracking-wider rounded-md"
            data-testid="go-active-tickets"
          >
            {activeCount > 1 ? "Mes tickets" : "Mon ticket"}
          </Link>
        </div>
      )}

      <div className={c.image ? "grid md:grid-cols-2 gap-10" : ""}>
        {c.image && (
          <div className="aspect-[4/3] overflow-hidden bg-surface">
            <img src={c.image} alt={c.name} className="h-full w-full object-cover" />
          </div>
        )}
        <div>
          <p className="overline mb-2">{c.type}</p>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">{c.name}</h1>
          <div className="mt-5 space-y-2 text-subtle">
            <p className="flex items-center gap-2"><MapPin size={16} /> {c.address}, {c.postalCode} {c.city}</p>
          </div>

          <div className="mt-8 grid grid-cols-2 border border-border">
            {(() => {
              // Status / wait / ETA must reflect the SELECTED queue, not the
              // establishment-wide aggregate (a closed queue could otherwise
              // appear "OUVERT" if another queue of the same commerce is open).
              const selQ = queues.find((q) => q.id === selectedQueueId);
              const queueOpen = selQ ? (selQ.isOpen !== false && !selQ.isSuspended) : c.isOpen;
              const queueSuspended = !!(selQ && selQ.isSuspended);
              const waitingCount = selQ ? (selQ.waitingCount ?? 0) : c.waitingCount;
              const estimatedWait = selQ ? (selQ.estimatedWait ?? 0) : c.estimatedWait;
              const statusLabel = queueSuspended ? "SUSPENDUE" : queueOpen ? "OUVERTE" : "FERMÉE";
              const statusColor = queueSuspended
                ? "text-[#F18F18]"
                : queueOpen
                ? "text-[#0A7F2E]"
                : "text-destructive";
              return (
                <>
                  <div className="p-5 border-r border-border">
                    <p className="overline">Statut</p>
                    <p className={`mt-2 text-lg font-bold ${statusColor}`} data-testid="commerce-status">
                      {statusLabel}
                    </p>
                  </div>
                  <div className="p-5">
                    <p className="overline">File{selQ?.name ? ` · ${selQ.name}` : ""}</p>
                    <p className="mt-2 text-lg font-bold text-ink flex items-center gap-2" data-testid="waiting-count">
                      <Users size={16} /> {waitingCount} en attente
                    </p>
                  </div>
                  <div className="p-5 border-t border-border col-span-2">
                    <p className="overline">Attente estimée</p>
                    <p className="mt-2 font-mono text-4xl font-bold text-ink">
                      {estimatedWait}<span className="text-base text-subtle font-normal"> min</span>
                    </p>
                  </div>
                </>
              );
            })()}
          </div>

          <div className="mt-8 space-y-3">
            {queues.length > 0 && (
              <div data-testid="queue-selector">
                {queues.length >= 1 && (
                  <>
                    <p className="text-xs uppercase tracking-wider font-bold text-subtle mb-2">
                      {queues.length === 1 ? "File disponible" : "Choisir une file"}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {queues.map((q) => {
                        // Spec utilisateur 2026-02 : files ouvertes en fond
                        // vert léger, files fermées en fond rouge léger,
                        // files suspendues en fond orange léger.
                        // Le bouton sélectionné garde un ring + un fond plus
                        // saturé pour bien le distinguer des autres.
                        const isSel = selectedQueueId === q.id;
                        const isSusp = !!q.isSuspended;
                        const isClosed = !isSusp && q.isOpen === false;
                        const variantCls = isSusp
                          ? (isSel
                              ? "bg-[#F18F18]/20 border-[#F18F18] ring-2 ring-[#F18F18] text-[#A85C00]"
                              : "bg-[#FFF6E6] border-[#F18F18]/40 text-[#A85C00] hover:border-[#F18F18]")
                          : isClosed
                            ? (isSel
                                ? "bg-[#FDE2E4] border-[#D7263D] ring-2 ring-[#D7263D] text-[#A8001D]"
                                : "bg-[#FFF1F2] border-[#D7263D]/40 text-[#A8001D] hover:border-[#D7263D]")
                            : (isSel
                                ? "bg-[#C9F2D3] border-[#0A7F2E] ring-2 ring-[#0A7F2E] text-[#0A4D1C]"
                                : "bg-[#ECFBEF] border-[#0A7F2E]/40 text-[#0A4D1C] hover:border-[#0A7F2E]");
                        return (
                          <button
                            key={q.id}
                            type="button"
                            onClick={() => setSelectedQueueId(q.id)}
                            className={`px-3 h-10 text-sm font-bold border transition-colors ${variantCls}`}
                            data-testid={`queue-choice-${q.id}`}
                          >
                            {q.name}
                            <span className={`ml-2 inline-block w-1.5 h-1.5 rounded-full ${isSusp ? "bg-[#F18F18] animate-pulse" : isClosed ? "bg-[#D7263D]" : "bg-[#0A7F2E]"}`} />
                            <span className="ml-1 text-[10px] font-mono opacity-80">
                              {q.waitingCount}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                {(() => {
                  const selQ = queues.find((q) => q.id === selectedQueueId);
                  if (!selQ) return null;
                  return (
                    <>
                      {selQ.customMessage && (() => {
                        // Spec utilisateur Feb 2026 — couleur du message
                        // personnalisé appliquée au texte également côté
                        // visiteur (auparavant `text-ink` forçait le noir).
                        const msgColorMap = {
                          blue:  "#1B5BFF",
                          red:   "#D7263D",
                          green: "#0A7F2E",
                          black: "#0A0F1E",
                        };
                        const msgColor = msgColorMap[selQ.customMessageColor] || msgColorMap.blue;
                        return (
                          <div className="border-l-2 bg-surface/60 px-3 py-2 mb-3" style={{ borderLeftColor: msgColor }} data-testid="queue-custom-message">
                            <p className="text-xs leading-relaxed whitespace-pre-line" style={{ color: msgColor }}>
                              <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
                              {selQ.customMessage}
                            </p>
                          </div>
                        );
                      })()}
                      {selQ.isSuspended ? (
                        <div className="border-2 border-[#F18F18] bg-[#F18F18]/10 px-3 py-3 mb-3 flex flex-col gap-2" data-testid="queue-suspended-banner">
                          <p className="font-display font-black text-[#A85C00] uppercase tracking-wide text-sm md:text-base text-center leading-tight">
                            File suspendue par l'établissement. Veuillez patienter SVP.
                          </p>
                          <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="self-center inline-flex items-center justify-center h-10 px-5 bg-[#F18F18] text-white font-bold tracking-tight hover:bg-[#D87A0A]"
                            data-testid="queue-refresh-btn"
                          >
                            Actualiser
                          </button>
                        </div>
                      ) : myStatus && myStatus.canTake === false ? (
                        <div className="border-2 border-destructive bg-destructive/5 px-3 py-3 mb-3 flex flex-col gap-2" data-testid="queue-already-served-banner">
                          <p className="font-display font-black text-destructive uppercase tracking-wide text-sm md:text-base text-center leading-tight flex items-center justify-center gap-2">
                            <Lock size={16} /> Déjà reçu sur cette file
                          </p>
                          <p className="text-sm text-center text-ink leading-relaxed">
                            {myStatus.message}
                          </p>
                          {myStatus.lastTicketNumber != null && (
                            <p className="text-xs text-center text-subtle font-mono">
                              Dernier ticket validé : n°{myStatus.lastTicketNumber}
                            </p>
                          )}
                        </div>
                      ) : selQ.isOpen === false && (
                        <div className="border-2 border-destructive bg-destructive/5 px-3 py-2 mb-3 text-sm font-bold text-destructive flex items-center gap-2" data-testid="queue-closed-banner">
                          <Lock size={14} /> Cette file est actuellement fermée.
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {(() => {
              const selQ = queues.find((q) => q.id === selectedQueueId);
              const queueClosed = selQ ? selQ.isOpen === false : false;
              const queueSuspended = !!selQ?.isSuspended;
              // Drain mode (fork 19) : queue is technically still open
              // to finish its remaining tickets but no new ticket can
              // be taken. We treat it like a soft "suspended" for the
              // visitor CTA.
              const queueDraining = !!selQ?.drainMode;
              const queueDrainReason = selQ?.drainReason || null;
              const alreadyServed = !!(myStatus && myStatus.canTake === false);
              const blockedSameQueue = !!ticketOnSelectedQueue;
              // Establishment accounts can browse queues but cannot take a
              // visitor ticket — they must log out first (per 2026-02 spec).
              const blockedAsMerchant = role === "commerce";
              // Special case : merchant account is being created but the email
              // hasn't been verified yet → the account is technically "pending".
              // In that case the user should either complete verification or
              // cancel the pending creation before being allowed to take a ticket.
              const merchantPending = blockedAsMerchant && commerce?.emailVerified === false;
              return (
                <Button
                  onClick={takeTicket}
                  disabled={creating || blockedAsMerchant || blockedSameQueue || hasReachedMax || queueClosed || queueDraining || alreadyServed}
                  className="w-full min-h-14 h-auto py-2 bg-brand hover:bg-brand-hover rounded-md text-white font-medium text-sm sm:text-base leading-snug whitespace-normal break-words text-center disabled:opacity-60"
                  data-testid="take-ticket-btn"
                  title={
                    merchantPending
                      ? "Un compte établissement est en cours de création. Confirmez votre email ou annulez la création pour prendre un ticket."
                      : blockedAsMerchant
                        ? "Déconnectez-vous de votre compte établissement pour pouvoir prendre un ticket"
                        : queueDraining
                          ? "Cette file ne prend plus de nouveaux tickets — fermeture programmée."
                          : queueSuspended
                            ? "La file est suspendue par l'établissement. Cliquez pour plus d'informations."
                            : undefined
                  }
                >
                  <Ticket size={18} className="mr-2 shrink-0" />
                  {merchantPending
                    ? "Compte en cours de création"
                    : blockedAsMerchant
                    ? "Déconnectez-vous de votre compte établissement pour pouvoir prendre un ticket"
                    : alreadyServed
                    ? "Déjà reçu sur cette file"
                    : queueDraining
                    ? (queueDrainReason === "expired_24h"
                        ? "File expirée — plus de nouveaux tickets"
                        : "File en fermeture — plus de nouveaux tickets")
                    : queueClosed
                    ? "File fermée"
                    : blockedSameQueue
                    ? "Ticket déjà actif sur cette file"
                    : hasReachedMax
                    ? `Limite de ${MAX_ACTIVE_TICKETS} tickets atteinte`
                    : "Prendre un ticket · Gratuit"}
                </Button>
              );
            })()}

            {/* Hint dédié au cas "compte établissement en cours de création".
               Affiché sous le bouton CTA quand l'email n'est pas encore vérifié,
               pour orienter clairement l'utilisateur vers l'action attendue. */}
            {role === "commerce" && commerce?.emailVerified === false && (
              <div
                className="mt-2 border border-[#F18F18] bg-[#FFF8EB] p-3 rounded-md text-sm leading-snug"
                role="status"
                data-testid="merchant-pending-hint"
              >
                <p className="font-bold text-[#A65800] mb-1">⏳ Un compte établissement est en cours de création.</p>
                <p className="text-ink mb-2">
                  Confirmez la création de votre compte (lien envoyé par email) <strong>ou annulez-la</strong> pour pouvoir prendre un ticket en tant que visiteur.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to={`/check-email?email=${encodeURIComponent(commerce?.email || "")}&role=commerce`}
                    className="inline-flex items-center justify-center h-9 px-3 bg-ink hover:bg-brand text-white font-bold text-xs rounded-md transition-colors"
                    data-testid="merchant-pending-verify-link"
                  >
                    Confirmer mon email
                  </Link>
                </div>
              </div>
            )}

            {/* RGPD: explicit opt-in to save payment method (only when paid mode) */}
            {role === "client" && !ticketOnSelectedQueue && !hasReachedMax && !isFreeMode && (
              <div className="flex items-start gap-3 pt-1" data-testid="save-card-block">
                <Checkbox
                  id="save-card"
                  checked={saveCard}
                  onCheckedChange={(v) => setSaveCard(!!v)}
                  className="mt-0.5"
                  data-testid="save-card-checkbox"
                />
                <label htmlFor="save-card" className="text-xs text-ink leading-snug cursor-pointer">
                  <span className="font-medium">Enregistrer ma carte pour les prochains paiements</span>
                  <span className="block text-[11px] text-subtle mt-0.5">
                    Tokenisation sécurisée via Stripe — aucune donnée de carte stockée chez BypassGo. Vous pouvez supprimer votre carte à tout moment dans <Link to="/my-cards" className="underline text-brand">Mes moyens de paiement</Link>.
                  </span>
                </label>
              </div>
            )}

            {!isFreeMode && (
              <PaymentMethodsBadge testId="payment-methods-hint" />
            )}
            {isFreeMode && (
              <div className="flex items-center justify-center gap-2 text-xs text-[#0A7F2E] font-bold" data-testid="free-mode-hint">
                <ShieldCheck size={12} /> Mode gratuit · Aucun paiement requis
              </div>
            )}

            <p className="text-xs text-subtle text-center leading-relaxed mt-2">
              Suivez votre tour en temps réel et venez uniquement au bon moment.
            </p>
            {(() => {
              // Show the "closed" banner based on the SELECTED queue, not
              // on the legacy commerce-level flag (which can drift). When
              // no queue exists at all, fall back to `c.isOpen`.
              const selQ = queues.find((q) => q.id === selectedQueueId);
              if (selQ) {
                if (selQ.isSuspended) {
                  return <p className="text-sm text-destructive text-center font-medium">Cette file est actuellement suspendue.</p>;
                }
                if (selQ.isOpen === false) {
                  return <p className="text-sm text-destructive text-center font-medium">Cette file est actuellement fermée par l'établissement.</p>;
                }
                return null;
              }
              return !c.isOpen
                ? <p className="text-sm text-destructive text-center font-medium">L'établissement est actuellement fermé.</p>
                : null;
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentBadge({ label, icon }) {
  return (
    <span className="inline-flex items-center gap-1 border border-border px-1.5 py-0.5 bg-white">
      {icon}
      <span className="font-medium text-ink text-[10px]">{label}</span>
    </span>
  );
}
